import React from 'react';
import { uuid } from '../../../utils/utils';
import EventSequenceService from '../../../services/event-sequence.service';
import { toast } from 'react-toastify';
import {EventSequenceSummaryEditorProps} from '../../../@types/editor-props';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { ImportanceMeasureType, MethodType, ScopeType, ImportanceMeasure, SafetyModelElementImportanceMeasures } from '../../../@types/sensitivity-analysis';
import {
    AppBar, Backdrop, Button, Card, Checkbox, FormControlLabel, FormGroup,
    FormLabel, FormControl,
    Paper, Radio, RadioGroup, Stack, TextField, Typography
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { InputAdornment } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import Toolbar from '@mui/material/Toolbar';
import BarChartIcon from '@mui/icons-material/BarChart';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DownloadTableExcel } from 'react-export-table-to-excel';

type Order = 'asc' | 'desc';
interface SensitivityDataTable {
    uniqueId: string | null;
    name: string;
    accidentLikelihoodSensitivity_pd: string;
    accidentLikelihoodSensitivity_bi: string;
    accidentLikelihoodSensitivity_raw: string;
    accidentLikelihoodSensitivity_fv: string;
    accidentRiskSensitivity_pd: string;
    accidentRiskSensitivity_bi: string;
    accidentRiskSensitivity_raw: string;
    accidentRiskSensitivity_fv: string;
    occurrenceCount: number;
}

const interfaceTranslation = {
    PARTIAL_DERIVATIVE: 'pd',
    BIRNBAUM_IMPORTANCE: 'bi',
    RISK_ACHIEVEMENT_WORTH: 'raw',
    FUSSEL_VESELY: 'fv',
};

const transformImportanceMeasures = (importanceMeasures: Array<ImportanceMeasure>, precision: number): any => {
    return importanceMeasures.reduce(
        (prev, measure) => {
            const { type } = measure;
            const field = interfaceTranslation[type];
            const likelihood = parseFloat(measure.likelihoodMeasure);
            const risk = parseFloat(measure.riskMeasure);
            return {
                accidentLikelihoodSensitivity: {
                    ...prev.accidentLikelihoodSensitivity,
                    [field]: isNaN(likelihood) ? 'No score' : likelihood.toExponential(precision),
                },
                accidentRiskSensitivity: {
                    ...prev.accidentRiskSensitivity,
                    [field]: isNaN(risk) ? 'No score' : risk.toExponential(precision),
                },
            };
        },
        {
            accidentLikelihoodSensitivity: {},
            accidentRiskSensitivity: {},
        }
    );
};

function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator<Key extends keyof SensitivityDataTable>(
    order: Order,
    orderBy: Key,
): (
  a: SensitivityDataTable,
  b: SensitivityDataTable,
) => number {
    return order === 'desc'
        ? (a, b): number => descendingComparator(a, b, orderBy)
        : (a, b): number => -descendingComparator(a, b, orderBy);
}

export default function SensitivityAnalysisEditor(props: EventSequenceSummaryEditorProps): React.ReactElement {
    const { eventSequenceSummary, isActive } = props;
    const [importanceMeasureTypes, setImportanceMeasureTypes] = React.useState<ImportanceMeasureType[]>([]);
    const [sensitivityAnalysisDataTable, setSensitivityAnalysisDataTable] = React.useState<SensitivityDataTable[]>([]);
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof SensitivityDataTable>('name');   
    const [precision, setPrecision] = React.useState<number>(2);
    const [increase, setIncrease] = React.useState<number>(1);
    const [method, setMethod] = React.useState<MethodType>('SINGLE_LABEL_ANALYSIS');
    const [tableMethod, setTableMethod] = React.useState<MethodType>('SINGLE_LABEL_ANALYSIS');
    const [scope, setScope] = React.useState<ScopeType>('FAULT_TREE');
    const [measures, setMeasures] = React.useState<boolean[]>([true, false, false, false]);
    const [isRunning, setIsRunning] = React.useState<boolean>(false);
    const [searchTerm, setSearchTerm] = React.useState<string>('');
    const [originalResult, setOriginalResult] = React.useState<SafetyModelElementImportanceMeasures[]>([]);
    const [sidePanelOpen, setSidePanelOpen] = React.useState<boolean>(false);
    const uuidRef = React.useRef(uuid());
    const tableRef = React.useRef(null);

    const handleRequestSort = (
        property: keyof SensitivityDataTable,
    ): void => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };    
    
    const handlePrecisionChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        let val = parseInt(event.target.value);
        if (val && val > 0) {
            setPrecision(val);
        }
    }
    const handleMethodChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setMethod((event.target as HTMLInputElement).value as MethodType);
    };

    const handleScopeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setScope((event.target as HTMLInputElement).value as ScopeType);
    };

    const handleMeasureChangePD = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setMeasures([event.target.checked, measures[1], measures[2], measures[3]]);
    };

    const handleMeasureChangeBI = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setMeasures([measures[0], event.target.checked, measures[2], measures[3]]);
    };

    const handleMeasureChangeRAW = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setMeasures([measures[0], measures[1], event.target.checked, measures[3]]);
    };

    const handleMeasureChangeFV = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setMeasures([measures[0], measures[1], measures[2], event.target.checked]);
    };


    const toggleSidePanel = (): void => {
        setSidePanelOpen(!sidePanelOpen);
    }

    const onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchTerm(event.target.value);
    }

    const handleSensitivityAnalysis = (): void => {
        let importanceMeasures: Array<ImportanceMeasureType> = [];
        if (measures[0]) importanceMeasures.push('PARTIAL_DERIVATIVE');
        if (measures[1]) importanceMeasures.push('BIRNBAUM_IMPORTANCE');
        if (measures[2]) importanceMeasures.push('RISK_ACHIEVEMENT_WORTH');
        if (measures[3]) importanceMeasures.push('FUSSEL_VESELY');
        setIsRunning(true);
        EventSequenceService.fetchSensitivityAnalysis(eventSequenceSummary.id, method, scope, increase, importanceMeasures).then((response) => {
            if (response.type === 'SINGLE_LABEL_ANALYSIS')
                response.safetyModelElementImportanceMeasures.sort((a, b) => a.uniqueId!.localeCompare(b.uniqueId!));
            else
                response.safetyModelElementImportanceMeasures.sort((a, b) => a.name.localeCompare(b.name));
            
            let result = response.safetyModelElementImportanceMeasures.filter((r) => {
                return r.uniqueId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    r.name.toLowerCase().includes(searchTerm.toLowerCase())
            });
            setOriginalResult(response.safetyModelElementImportanceMeasures);
            setImportanceMeasureTypes(response.importanceMeasureTypes);
            setTableMethod(response.type);
            setSidePanelOpen(false);
            
            let x:SensitivityDataTable[]  = result.map((data) => {
                const sensitivity = transformImportanceMeasures(data.importanceMeasures, precision);
                return {
                    uniqueId: data.uniqueId,
                    name: data.name,
                    accidentLikelihoodSensitivity_pd: sensitivity.accidentLikelihoodSensitivity.pd,
                    accidentLikelihoodSensitivity_bi: sensitivity.accidentLikelihoodSensitivity.bi,
                    accidentLikelihoodSensitivity_raw: sensitivity.accidentLikelihoodSensitivity.raw,
                    accidentLikelihoodSensitivity_fv: sensitivity.accidentLikelihoodSensitivity.fv,
                    accidentRiskSensitivity_pd: sensitivity.accidentRiskSensitivity.pd,
                    accidentRiskSensitivity_bi: sensitivity.accidentRiskSensitivity.bi,
                    accidentRiskSensitivity_raw: sensitivity.accidentRiskSensitivity.raw,
                    accidentRiskSensitivity_fv: sensitivity.accidentRiskSensitivity.fv,
                    occurrenceCount: data.occurrenceCount
                    
                }
            });
            setSensitivityAnalysisDataTable(x);
        }).catch((error) => {
            toast.error(`Failed to load event sequence: ${error}`);
        }).finally(() => {
            setIsRunning(false);
        });
    }


    React.useEffect(() => {
        if (isActive) {
            handleSensitivityAnalysis();
        }
    }, [isActive]);

    React.useEffect(() => {
        let filteredResult = originalResult.filter((r) => {
            return r.uniqueId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    r.name.toLowerCase().includes(searchTerm.toLowerCase())
        }) ?? [];
        
        let x:SensitivityDataTable[]  = filteredResult.map((data) => {
            const sensitivity = transformImportanceMeasures(data.importanceMeasures, precision);
            return {
                uniqueId: data.uniqueId,
                name: data.name,
                accidentLikelihoodSensitivity_pd: sensitivity.accidentLikelihoodSensitivity.pd,
                accidentLikelihoodSensitivity_bi: sensitivity.accidentLikelihoodSensitivity.bi,
                accidentLikelihoodSensitivity_raw: sensitivity.accidentLikelihoodSensitivity.raw,
                accidentLikelihoodSensitivity_fv: sensitivity.accidentLikelihoodSensitivity.fv,
                accidentRiskSensitivity_pd: sensitivity.accidentRiskSensitivity.pd,
                accidentRiskSensitivity_bi: sensitivity.accidentRiskSensitivity.bi,
                accidentRiskSensitivity_raw: sensitivity.accidentRiskSensitivity.raw,
                accidentRiskSensitivity_fv: sensitivity.accidentRiskSensitivity.fv,
                occurrenceCount: data.occurrenceCount                
            }
        });
        setSensitivityAnalysisDataTable(x);
    }, [searchTerm, precision]);

    
    React.useEffect(() => {
        setSensitivityAnalysisDataTable((p) => p.sort(getComparator(order, orderBy)));
    }, [order, orderBy]);

    return (
        <Paper id={uuidRef.current}   sx={{ height: '100%'}}>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isRunning}> 
                <CircularProgress color='inherit' />
            </Backdrop>
            
            <div className={`side-panel-content ${sidePanelOpen ? 'side-panel-open' : ''}`} style={{ height: '100%'}}>
                <AppBar position="static"
                    sx={{
                        width: '100%',
                        borderRadius: '0px 10px 0px 0px',
                        backgroundColor: '#fff',
                        color: '#808080'
                    }}
                >
                    <Toolbar
                        sx={[
                            {
                                pl: { sm: 2 },
                                pr: { xs: 1, sm: 1 },
                                flexGrow: 1
                            }
                        ]}
                    >
                        <Typography
                            component='div'
                            variant='h6'
                            color='inherit'
                            sx={{ flex: '1 1 100%' }}
                        >
                        Sensitivity Analysis Options
                        </Typography>
                        <IconButton
                            color="inherit"
                            component='button'
                            edge='end'
                            onClick={toggleSidePanel} >
                            <CloseIcon fontSize='small'/>
                        </IconButton>
                    </Toolbar>

                </AppBar>
                <FormGroup className='formStyle' style={{width: '100%'}}>
                    <Stack className='formControl' style={{ width: '100%', paddingLeft: 10 }}>
                        <Box sx={{ display: 'flex', direction: 'row', justifyContent: 'space-evenly'}}>
                            <Card variant="outlined" sx={{mr: 1}}>
                                <FormControl sx={{m: 0.5}}>
                                    <FormLabel id={`method-label-${uuidRef.current}`} component="legend">Analysis Method</FormLabel>
                                    <RadioGroup
                                        aria-labelledby={`method-label-${uuidRef.current}`}
                                        value={method}
                                        onChange={handleMethodChange}
                                    >
                                        <FormControlLabel value="SINGLE_LABEL_ANALYSIS" control={<Radio />} label="Single Label Analysis" />
                                        <FormControlLabel value="COMMON_LABEL_ANALYSIS" control={<Radio />} label="Common Label Analysis" />
                                    </RadioGroup>
                                </FormControl>
                            </Card>
                            <Card variant="outlined" sx={{mr: 2}}>
                                <FormControl sx={{m: 0.5}}>
                                    <FormLabel id={`scope-label-${uuidRef.current}`} component="legend">Scope</FormLabel>
                                    <RadioGroup
                                        aria-labelledby={`scope-label-${uuidRef.current}`}
                                        value={scope}
                                        onChange={handleScopeChange}
                                    >
                                        <FormControlLabel value="FAULT_TREE" control={<Radio />} label="Fault Tree" />
                                        <FormControlLabel value="EVENT_SEQUENCE" control={<Radio />} label="Event Sequence" />
                                    </RadioGroup>
                                </FormControl>
                            </Card>

                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                            <Card variant="outlined">
                                <FormControl sx={{m: 0.5}}>
                                    <FormLabel
                                        id={`measure-label-${uuidRef.current}`}
                                        component="legend"
                                    >
                                        Importance Measures
                                    </FormLabel>
                                    <FormGroup 
                                        aria-labelledby={`measure-label-${uuidRef.current}`}
                                    >
                                        <FormControlLabel
                                            value="PARTIAL_DERIVATIVE"
                                            control={<Checkbox checked={measures[0]}
                                                onChange={handleMeasureChangePD} />}
                                            label="Partial Derivative (PD)" />
                                        <TextField
                                            id={`increase-input-${uuidRef.current}`}
                                            label='Increase (%)'
                                            aria-label='increase'
                                            type='number'
                                            size='small'
                                            onChange={(event): void => setIncrease(parseInt(event.target.value))}
                                            defaultValue={increase}
                                            inputProps={{
                                                min: 0,
                                                step: 1,
                                                'tabIndex': 0,
                                                'aria-labelledby': `increase-input-${uuidRef.current}`,
                                                'aria-label': 'Increase',
                                            }}
                                            sx={{ ml: 4, mb: 1, maxWidth: 100 }}
                                        />
                                    
                                        <FormControlLabel value="BIRNBAUM_IMPORTANCE" control={<Checkbox checked={measures[1]} onChange={handleMeasureChangeBI}  />} label="Birnbaum Importance (BI)" />
                                        <FormControlLabel value="RISK_ACHIEVEMENT_WORTH" control={<Checkbox checked={measures[2]} onChange={handleMeasureChangeRAW}  />} label="Risk Achievement Worth (RAW)" />
                                        <FormControlLabel value="FUSSEL_VESELY" control={<Checkbox checked={measures[3]} onChange={handleMeasureChangeFV}  />} label="Fussel-Vesely (FV)" />
                                    </FormGroup>
                                </FormControl>

                            </Card>
                        </Box>
                        <hr />
                    </Stack>
                    <Button
                        className='formBtn'
                        id={`run-uncertainty-analysis-${uuidRef.current}`}
                        variant='outlined'
                        color='primary'
                        onClick={(): void => { handleSensitivityAnalysis(); }}
                        startIcon={<BarChartIcon />}
                        sx={{ maxWidth: '200px', margin: 'auto' }}
                        disabled={measures.every(m => !m)}
                    >
                        Analyze
                    </Button>
                </FormGroup>              
            </div>
            <div className={`side-panel-handle touch-action ${sidePanelOpen ? 'side-panel-open' : ''}`} onClick={toggleSidePanel} style={{ cursor: 'pointer' }}>Options</div>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 0.5, pr: 0 }}>
                <Typography variant='h5' color='primary' sx={{ ml: 2, mt: 1 }}>
                        Sensitivity Analysis
                </Typography>

                <Box sx={{ flexGrow: 1 }}></Box>
                <TextField
                    id={`precision-input-${uuidRef.current}`}
                    label='Precision'
                    aria-label='precision'
                    type='number'
                    size='small'
                    onChange={(event): void => handlePrecisionChange(event)}
                    defaultValue={precision}
                    inputProps={{
                        min: 0,
                        step: 1,
                        'tabIndex': 0,
                        'aria-labelledby': `precision-input-${uuidRef.current}`,
                        'aria-label': 'precision',                                               
                    }}
                    sx={{mr: 0.5, mt: 1, maxWidth: 100 }}
                />

                <TextField
                    label='Filter'
                    aria-label='search field'
                    type='text'
                    variant='outlined'
                    size='small'
                    id={`search-box-${uuidRef.current}`}
                    onChange={onSearchTextChange}
                    sx={{
                        mr: 0.5, mt: 1,
                    }}

                    slotProps={{
                        input: {
                            'role': 'searchbox',
                            'tabIndex': 0,
                            'aria-labelledby': `search-box-${uuidRef.current}`,
                            'aria-label': 'search',
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton aria-label='search icon' onClick={():void  => {}}>
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }
                    }}
                />
                <DownloadTableExcel
                    filename={`sensitivity_analysis_${eventSequenceSummary.id}`}
                    sheet='sensitivity analysis'
                    currentTableRef={tableRef.current}
                >
                    <Button
                        className='formBtn'
                        id={`export-${uuidRef.current}`}
                        variant='outlined'
                        onClick={(): void => {  }}
                        startIcon={<FileDownloadIcon />}
                        sx={{maxWidth: '100px', mr: 2, mt: 1,}}
                    >
                        Export
                    </Button>

                </DownloadTableExcel>     
            </Box>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)'}}>
                <Table stickyHeader aria-label="sticky table" ref={tableRef} className='table'>
                    <TableHead>
                        <TableRow>
                            {tableMethod === 'SINGLE_LABEL_ANALYSIS' &&
                                <TableCell
                                    rowSpan={2}
                                    style={{ minWidth: 150 }}
                                    sortDirection={orderBy === 'uniqueId' ? order : false}
                                >
                                    <TableSortLabel
                                        active={orderBy === 'uniqueId'}
                                        direction={orderBy === 'uniqueId' ? order : 'asc'}
                                        onClick={(): void => handleRequestSort('uniqueId')}
                                    >
                                        Unique Id
                                    </TableSortLabel>
                                </TableCell>}
                            <TableCell
                                rowSpan={2}
                                style={{ minWidth: 400 }}
                                sortDirection={orderBy === 'name' ? order : false}
                            >
                                <TableSortLabel
                                    active={orderBy === 'name'}
                                    direction={orderBy === 'name' ? order : 'asc'}
                                    onClick={(): void => handleRequestSort('name')}
                                >
                                    Name
                                </TableSortLabel>                             
                            </TableCell>
                            <TableCell align='center' colSpan={importanceMeasureTypes.length}>
                                Accident Likelihood Sensitivity
                            </TableCell>
                            <TableCell align='center' colSpan={importanceMeasureTypes.length}>
                                Accident Risk Sensitivity
                            </TableCell>
                            {tableMethod === 'COMMON_LABEL_ANALYSIS' &&
                                <TableCell
                                    align='center'
                                    rowSpan={2}
                                    sortDirection={orderBy === 'occurrenceCount' ? order : false}
                                >
                                    <TableSortLabel
                                        active={orderBy === 'occurrenceCount'}
                                        direction={orderBy === 'occurrenceCount' ? order : 'asc'}
                                        onClick={(): void => handleRequestSort('occurrenceCount')}
                                    >
                                        Number of Occurrences
                                    </TableSortLabel>                                                             
                                </TableCell>}
                        </TableRow>
                        <TableRow >
                            {importanceMeasureTypes.includes('PARTIAL_DERIVATIVE') &&
                                <TableCell
                                    align='center'
                                    style={{ top: 57, minWidth: 76 }}
                                    sortDirection={orderBy === 'accidentLikelihoodSensitivity_pd' ? order : false}
                                >
                                    <TableSortLabel
                                        active={orderBy === 'accidentLikelihoodSensitivity_pd'}
                                        direction={orderBy === 'accidentLikelihoodSensitivity_pd' ? order : 'asc'}
                                        onClick={(): void => handleRequestSort('accidentLikelihoodSensitivity_pd')}
                                    >
                                        PD
                                    </TableSortLabel>                                                             
                                </TableCell>}
                            {importanceMeasureTypes.includes('BIRNBAUM_IMPORTANCE') &&
                                 <TableCell
                                     align='center'
                                     style={{ top: 57, minWidth: 76 }}
                                     sortDirection={orderBy === 'accidentLikelihoodSensitivity_bi' ? order : false}
                                 >
                                     <TableSortLabel
                                         active={orderBy === 'accidentLikelihoodSensitivity_bi'}
                                         direction={orderBy === 'accidentLikelihoodSensitivity_bi' ? order : 'asc'}
                                         onClick={(): void => handleRequestSort('accidentLikelihoodSensitivity_bi')}
                                     >
                                        BI
                                     </TableSortLabel>                                                             
                                 </TableCell>}
                            {importanceMeasureTypes.includes('RISK_ACHIEVEMENT_WORTH') &&
                                <TableCell
                                    align='center'
                                    style={{ top: 57, minWidth: 76 }}
                                    sortDirection={orderBy === 'accidentLikelihoodSensitivity_raw' ? order : false}
                                >
                                    <TableSortLabel
                                        active={orderBy === 'accidentLikelihoodSensitivity_raw'}
                                        direction={orderBy === 'accidentLikelihoodSensitivity_raw' ? order : 'asc'}
                                        onClick={(): void => handleRequestSort('accidentLikelihoodSensitivity_raw')}
                                    >
                                        RAW
                                    </TableSortLabel>                                                             
                                </TableCell>}
                            {importanceMeasureTypes.includes('FUSSEL_VESELY') &&
                                <TableCell
                                    align='center'
                                    style={{ top: 57, minWidth: 76 }}
                                    sortDirection={orderBy === 'accidentLikelihoodSensitivity_fv' ? order : false}
                                >
                                    <TableSortLabel
                                        active={orderBy === 'accidentLikelihoodSensitivity_fv'}
                                        direction={orderBy === 'accidentLikelihoodSensitivity_fv' ? order : 'asc'}
                                        onClick={(): void => handleRequestSort('accidentLikelihoodSensitivity_fv')}
                                    >
                                        FV
                                    </TableSortLabel>                                                             
                                </TableCell>}
                            {importanceMeasureTypes.includes('PARTIAL_DERIVATIVE') &&
                                <TableCell
                                    align='center'
                                    style={{ top: 57, minWidth: 76 }}
                                    sortDirection={orderBy === 'accidentRiskSensitivity_pd' ? order : false}
                                >
                                    <TableSortLabel
                                        active={orderBy === 'accidentRiskSensitivity_pd'}
                                        direction={orderBy === 'accidentRiskSensitivity_pd' ? order : 'asc'}
                                        onClick={(): void => handleRequestSort('accidentRiskSensitivity_pd')}
                                    >
                                        PD
                                    </TableSortLabel>                                                             
                                </TableCell>}
                            {importanceMeasureTypes.includes('BIRNBAUM_IMPORTANCE') &&
                                 <TableCell
                                     align='center'
                                     style={{ top: 57, minWidth: 76 }}
                                     sortDirection={orderBy === 'accidentRiskSensitivity_bi' ? order : false}
                                 >
                                     <TableSortLabel
                                         active={orderBy === 'accidentRiskSensitivity_bi'}
                                         direction={orderBy === 'accidentRiskSensitivity_bi' ? order : 'asc'}
                                         onClick={(): void => handleRequestSort('accidentRiskSensitivity_bi')}
                                     >
                                        BI
                                     </TableSortLabel>                                                             
                                 </TableCell>}
                            {importanceMeasureTypes.includes('RISK_ACHIEVEMENT_WORTH') &&
                                <TableCell
                                    align='center'
                                    style={{ top: 57, minWidth: 76 }}
                                    sortDirection={orderBy === 'accidentRiskSensitivity_raw' ? order : false}
                                >
                                    <TableSortLabel
                                        active={orderBy === 'accidentRiskSensitivity_raw'}
                                        direction={orderBy === 'accidentRiskSensitivity_raw' ? order : 'asc'}
                                        onClick={(): void => handleRequestSort('accidentRiskSensitivity_raw')}
                                    >
                                        RAW
                                    </TableSortLabel>                                                             
                                </TableCell>}
                            {importanceMeasureTypes.includes('FUSSEL_VESELY') &&
                                <TableCell
                                    align='center'
                                    style={{ top: 57, minWidth: 76 }}
                                    sortDirection={orderBy === 'accidentRiskSensitivity_fv' ? order : false}
                                >
                                    <TableSortLabel
                                        active={orderBy === 'accidentRiskSensitivity_fv'}
                                        direction={orderBy === 'accidentRiskSensitivity_fv' ? order : 'asc'}
                                        onClick={(): void => handleRequestSort('accidentRiskSensitivity_fv')}
                                    >
                                        FV
                                    </TableSortLabel>                                                             
                                </TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sensitivityAnalysisDataTable.map((row, index) => {
                            return (
                                <TableRow 
                                    key={index}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    {tableMethod === 'SINGLE_LABEL_ANALYSIS' && <TableCell>{row.uniqueId}</TableCell>}
                                    <TableCell>{row.name}</TableCell>
                                    {row.accidentLikelihoodSensitivity_pd && <TableCell align='center'>{row.accidentLikelihoodSensitivity_pd}</TableCell>}
                                    {row.accidentLikelihoodSensitivity_bi && <TableCell align='center'>{row.accidentLikelihoodSensitivity_bi}</TableCell>}
                                    {row.accidentLikelihoodSensitivity_raw && <TableCell align='center'>{row.accidentLikelihoodSensitivity_raw}</TableCell>}
                                    {row.accidentLikelihoodSensitivity_fv && <TableCell align='center'>{row.accidentLikelihoodSensitivity_fv}</TableCell>}
                                    {row.accidentRiskSensitivity_pd && <TableCell align='center'>{row.accidentRiskSensitivity_pd}</TableCell>}
                                    {row.accidentRiskSensitivity_bi && <TableCell align='center'>{row.accidentRiskSensitivity_bi}</TableCell>}
                                    {row.accidentRiskSensitivity_raw && <TableCell align='center'>{row.accidentRiskSensitivity_raw}</TableCell>}
                                    {row.accidentRiskSensitivity_fv && <TableCell align='center'>{row.accidentRiskSensitivity_fv}</TableCell>}
                                    {tableMethod === 'COMMON_LABEL_ANALYSIS' && <TableCell align='center'>{row.occurrenceCount}</TableCell>}
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}